import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { buttonClass } from '@/components/ui/Button';
import { SelectorIdioma } from '@/components/ui/SelectorIdioma';
import { useI18n } from '@/hooks/useI18n';
import { RUTA_AREA } from '@/features/inversionistas/rutas';
import { aDondeVaElArea, enlaceALaPlataforma } from '@/router/portadaPorHostname';
import styles from './SiteHeader.module.css';

/**
 * Los enlaces de la navegación son anclas dentro del home (`#planes`, ...).
 * Desde una ruta que no es el home (una página legal, `/contacto`) un `#planes`
 * a secas no lleva a ningún lado, así que se prefijan con `/` para que
 * react-router navegue al home y el navegador resuelva el ancla.
 */
function anchorHref(hash: string, isHome: boolean) {
  return isHome ? hash : `/${hash}`;
}

export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const { t } = useI18n();
  const h = t.comun.header;

  /*
    El area vive en su propio hostname, y desde `www.` su API la bloquea por
    CORS. Asi que desde aqui el enlace es ABSOLUTO al host que funciona, y no
    un `<Link>` del router: un `<Link>` navegaria dentro de `www.` y dejaria a
    la persona en un login que se ve bien y no entra.

    `null` = ya estamos donde el area funciona --el propio
    `inversionistas.ilumetria.com`, o `localhost`-- y entonces si vale el
    `<Link>`, que no recarga la pagina.
  */
  const hostDelArea = aDondeVaElArea(window.location.hostname);
  const enlaceAlArea = hostDelArea ? `https://${hostDelArea}${RUTA_AREA}` : RUTA_AREA;

  /*
    La plataforma MICE --Platanometria-- es OTRO producto en OTRO origen:
    `mice-web`, servido en `plataforma.ilumetria.com`. Aqui no hay pantalla de
    login ni la va a haber; lo que entra es este enlace, y nada mas
    (DECISIONES-MICE-2026-09-28.md 8.2, del 2026-09-29).

    Por eso es SIEMPRE un `<a href>` absoluto y nunca un `<Link>`, sin el caso
    `null` que si tiene el area: un `<Link to="/login">` navegaria dentro de
    este bundle y caeria en la ruta retirada de `routes.tsx`, que redirige al
    home. La persona pulsaria "entrar" y volveria a la portada.

    Y el destino se DERIVA del hostname para que desde `dev.ilumetria.com` vaya
    a la plataforma de dev y no a la de produccion.
  */
  const enlaceALaPlataformaMice = enlaceALaPlataforma(window.location.hostname);

  /*
   * Aquí había un botón "Entrar" y hasta cuatro enlaces que dependían de la
   * sesión. Se fueron con el backend el 2026-09-18, y el motivo no era que
   * sobraran: el header se pinta en **todas** las páginas del sitio, así que un
   * "Entrar" olvidado sería una puerta tapiada ofrecida en cada pantalla, y el
   * sitio entero parecería roto por el enlace más visible que tiene.
   *
   * **"Inversionistas" vuelve a abrir una puerta el 2026-09-21, y esta vez
   * lleva a algo.** El área existe, tiene backend (`capital`) y responde: la
   * regla que echó al "Entrar" —enlaces a contenido que existe— se cumple.
   *
   * Va como `outline` y no como `primary` a propósito: "Agendar demo" es la
   * acción que el sitio le pide a un visitante cualquiera, y son muchos más
   * que los inversionistas. Dos botones llenos compiten y el de al lado pierde
   * fuerza.
   *
   * Y va **también en el panel móvil**. Un enlace que sólo existe en escritorio
   * es peor que ninguno: quien lo vio en su portátil y lo busca en el teléfono
   * cree que se rompió.
   */

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Cerrar el menú al cambiar de ruta: si no, queda abierto sobre la página nueva.
  useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <>
      <header className={[styles.header, scrolled && styles.scrolled].filter(Boolean).join(' ')}>
        <div className={['ilu-container', styles.inner].join(' ')}>
          <Link to="/" className={styles.logo} aria-label={h.logoLabel}>
            <img src="/logo/isologo.png" alt="" width={36} height={36} />
            <span>ILUMETRIA</span>
          </Link>

          <nav className={styles.nav} aria-label={h.navLabel}>
            {t.comun.navLinks.map((link) => (
              <a key={link.href} href={anchorHref(link.href, isHome)}>
                {link.label}
              </a>
            ))}
            <Link to="/contacto">{h.contacto}</Link>
          </nav>

          <div className={styles.actions}>
            {/* El selector cubre las 20 rutas que cuelgan de `SiteLayout`
                —legales y pantallas de sesión incluidas—; el portal de marcas
                monta el suyo porque va fuera de este layout. */}
            <SelectorIdioma className={styles.desktopOnly} />
            {hostDelArea ? (
              <a
                href={enlaceAlArea}
                className={[buttonClass({ variant: 'outline', size: 'sm' }), styles.desktopOnly].join(
                  ' ',
                )}
              >
                {h.inversionistas}
              </a>
            ) : (
              <Link
                to={RUTA_AREA}
                className={[buttonClass({ variant: 'outline', size: 'sm' }), styles.desktopOnly].join(
                  ' ',
                )}
              >
                {h.inversionistas}
              </Link>
            )}
            <a
              href={enlaceALaPlataformaMice}
              className={[buttonClass({ variant: 'outline', size: 'sm' }), styles.desktopOnly].join(
                ' ',
              )}
            >
              {h.plataforma}
            </a>
            <Link to="/contacto" className={buttonClass({ variant: 'primary', size: 'sm' })}>
              {h.agendarDemo}
            </Link>
            <button
              type="button"
              className={styles.hamburger}
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-label={menuOpen ? h.cerrarMenu : h.abrirMenu}
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className={styles.mobilePanel}>
          {t.comun.navLinks.map((link) => (
            <a key={link.href} href={anchorHref(link.href, isHome)} onClick={() => setMenuOpen(false)}>
              {link.label}
            </a>
          ))}
          <Link to="/contacto">{h.contacto}</Link>
          {hostDelArea ? (
            <a href={enlaceAlArea}>{h.inversionistas}</a>
          ) : (
            <Link to={RUTA_AREA}>{h.inversionistas}</Link>
          )}
          {/* Tambien en el panel: un enlace que solo existe en escritorio es
              peor que ninguno --quien lo vio en su portatil y lo busca en el
              telefono cree que se rompio--, y en movil el `desktopOnly` de
              arriba lo esconde. */}
          <a href={enlaceALaPlataformaMice}>{h.plataforma}</a>
          <SelectorIdioma variante="lista" />
        </div>
      )}
    </>
  );
}
